import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Queries/FetchBeneficiaryContacts';
import fetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/fetchBeneficiaryContacts';
import { PermissionEnum } from '@Application/Security/Roles';
import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';
import SubjectFactList from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectFactList';
import SubjectInformationPanel from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectInformationPanel';
import { openBeneficiarySidebar } from '@UI/Pages/Beneficiaries/Signals/beneficiarySidebarSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type BeneficiaryView from '@UI/Store/Beneficiaries/Views/BeneficiaryView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly beneficiary: BeneficiaryView;
};

/**
 * The dossier's information panel: birth date, address, referents and contacts as a definition list,
 * then the links to the screens that edit them and the "Modifier le dossier" trigger.
 *
 * Self-contained: it fetches the two lists it draws — the agency directory, to put names on the
 * referent ids the dossier carries, and the contacts — through `useStoreQuery`, so a list another part
 * of the screen already holds is read from the store instead of being requested twice.
 */
const BeneficiarySummaryAside: React.FC<Props> = ({ beneficiary }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const beneficiaryId = beneficiary.id;
	const facilityId = beneficiary.facilityId;
	const isArchived = beneficiary.isArchived;

	const { data: facilityUsers } = useFacilityUsers();
	const { data: contacts, error: contactsError } = useStoreQuery({
		selector: (state) => state.beneficiaryContactsStore.contacts,
		stopOn: (value) => value !== null && value.beneficiaryId === beneficiaryId,
		query: () =>
			dispatch(fetchBeneficiaryContacts(new FetchBeneficiaryContacts(facilityId, beneficiaryId))),
		deps: [facilityId, beneficiaryId],
	});

	const assignedUserCount = beneficiary.assignedUserIds.length;

	// A referent absent from the directory (account removed from the agency) still counts on the dossier:
	// the count is what says so, rather than a shorter list standing in for it.
	const referentsText = React.useMemo(() => {
		if (facilityUsers === null) {
			return 'Chargement…';
		}

		if (assignedUserCount === 0) {
			return 'Aucun référent affecté.';
		}

		const names = beneficiary.assignedUserIds.flatMap((userId) => {
			const user = facilityUsers.list.find((candidate) => candidate.id === userId);

			return user !== undefined ? [user.fullName] : [];
		});

		return names.length === assignedUserCount
			? names.join(', ')
			: `${assignedUserCount} référent(s) affecté(s)`;
	}, [facilityUsers, beneficiary.assignedUserIds, assignedUserCount]);

	const contactsText = React.useMemo(() => {
		if (contactsError !== null) {
			return "Les contacts n'ont pas pu être chargés.";
		}

		if (contacts === null) {
			return 'Chargement…';
		}

		if (contacts.list.length === 0) {
			return 'Aucun contact enregistré.';
		}

		return contacts.list
			.map((contact) =>
				contact.qualification !== null
					? `${contact.firstName} ${contact.lastName} (${contact.qualification})`
					: `${contact.firstName} ${contact.lastName}`,
			)
			.join(' · ');
	}, [contacts, contactsError]);

	const facts = React.useMemo(
		() => [
			{
				key: 'birthDate',
				label: 'Naissance',
				value: beneficiary.birthDate?.toDisplay() ?? 'Non renseignée',
			},
			{ key: 'homeAddress', label: 'Adresse', value: beneficiary.homeAddress ?? 'Non renseignée' },
			{ key: 'referents', label: 'Référente', value: referentsText },
			{ key: 'contacts', label: 'Contacts', value: contactsText },
		],
		[beneficiary.birthDate, beneficiary.homeAddress, referentsText, contactsText],
	);

	const handleContactsClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(SubjectAddress.forBeneficiary(beneficiaryId))}/contacts`);
	}, [navigate, beneficiaryId]);

	const handleMembersClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(SubjectAddress.forBeneficiary(beneficiaryId))}/members`);
	}, [navigate, beneficiaryId]);

	const handleEditClick = React.useCallback(() => {
		openBeneficiarySidebar(beneficiaryId);
	}, [beneficiaryId]);

	return (
		<SubjectInformationPanel title="Informations du bénéficiaire">
			<SubjectFactList facts={facts} />
			<div css={actionsStyle}>
				<ConditionalLoader data={!isArchived}>
					<Button
						type="button"
						severity="secondary"
						outlined
						size="sm"
						label="Modifier les contacts"
						onClick={handleContactsClick}
					/>
				</ConditionalLoader>
				<Button
					type="button"
					severity="secondary"
					outlined
					size="sm"
					label="Membres"
					onClick={handleMembersClick}
				/>
				<ConditionalLoader data={!isArchived}>
					<WithPermission permission={PermissionEnum.SUBJECT_WRITE}>
						<Button
							type="button"
							severity="secondary"
							outlined
							size="sm"
							label="Modifier le dossier"
							onClick={handleEditClick}
						/>
					</WithPermission>
				</ConditionalLoader>
			</div>
		</SubjectInformationPanel>
	);
};

export default BeneficiarySummaryAside;
